import { Box, CircularProgress, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import { ReactNode } from "react";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import { CustomButton } from "src/design/CustomButton";
import {
  colorBorder,
  colorBorderInfo,
  colorBackgroundInfo,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  iconBgInfo,
  iconColorInfo,
  radiusCard,
  shadowCard,
} from "src/design/tokens";

/**
 * Why the section behind this card is unavailable. `upgrade` is a sales path —
 * the restaurant's subscription does not bundle the capability. `permission` is
 * an internal one — the organization already pays for it, the viewer was just
 * not granted access.
 */
export type FeatureLockedVariant = "upgrade" | "permission";

interface FeatureLockedCardProps {
  variant: FeatureLockedVariant;
  /** Localized name of the locked module, injected into the default copy. */
  featureLabel: string;
  /** Overrides the variant's default heading. */
  title?: string;
  /** Overrides the variant's default explanatory message. */
  message?: string;
  /**
   * Called when the viewer asks for the upgrade. `null`/omitted renders the CTA
   * disabled — for callers with nothing to send the request against.
   */
  onAction?: (() => void) | null;
  /** Keeps the CTA disabled with a spinner while the request is in flight. */
  isActionLoading?: boolean;
  sx?: SxProps;
}

/**
 * The primary CTA is a solid dark button, so MUI's default disabled grey would
 * sink the label into the background. Every other disabled dark CTA in the app
 * (see the menu modals) dims the label to translucent white instead — same
 * value here so the button still reads as a button while it is unavailable.
 */
const DISABLED_LABEL_COLOR = "rgba(255,255,255,0.4)";

const VARIANT_CONFIG: Record<
  FeatureLockedVariant,
  {
    icon: ReactNode;
    iconBg: string;
    iconColor: string;
    border: string;
    background: string;
    titleKey: string;
    messageKey: string;
    /** Only the upgrade variant offers an action; access is requested offline. */
    actionKey: string | null;
  }
> = {
  upgrade: {
    icon: <WorkspacePremiumOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: iconBgInfo,
    iconColor: iconColorInfo,
    border: colorBorderInfo,
    background: colorBackgroundInfo,
    titleKey: "featureLocked.upgrade.title",
    messageKey: "featureLocked.upgrade.message",
    actionKey: "featureLocked.upgrade.action",
  },
  permission: {
    icon: <LockOutlinedIcon sx={{ fontSize: 22 }} />,
    iconBg: iconBgInfo,
    iconColor: colorTextMuted,
    border: colorBorder,
    background: colorSurface,
    titleKey: "featureLocked.permission.title",
    messageKey: "featureLocked.permission.message",
    actionKey: null,
  },
};

export const FeatureLockedCard = ({
  variant,
  featureLabel,
  title,
  message,
  onAction,
  isActionLoading = false,
  sx,
}: FeatureLockedCardProps) => {
  const { t } = useTranslation();
  const config = VARIANT_CONFIG[variant];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 1,
        height: "100%",
        backgroundColor: config.background,
        border: `1px solid ${config.border}`,
        borderRadius: radiusCard,
        boxShadow: shadowCard,
        px: { xs: 3, sm: 4 },
        py: { xs: 3, sm: 4 },
        ...sx,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          backgroundColor: config.iconBg,
          color: config.iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        {config.icon}
      </Box>

      <Typography
        sx={{ fontWeight: 700, fontSize: "16px", color: colorTextPrimary }}
      >
        {title ?? t(config.titleKey, { feature: featureLabel })}
      </Typography>

      <Typography
        sx={{ fontSize: "14px", color: colorTextMuted, maxWidth: 420 }}
      >
        {message ?? t(config.messageKey, { feature: featureLabel })}
      </Typography>

      {config.actionKey && (
        <CustomButton
          color="black"
          disabled={!onAction || isActionLoading}
          disabledColor={DISABLED_LABEL_COLOR}
          onClick={() => onAction?.()}
          startIcon={
            isActionLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{ mt: 1.5 }}
        >
          {t(config.actionKey)}
        </CustomButton>
      )}
    </Box>
  );
};
